"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResultBanner,
  PrintButton,
  ActionPlanner,
  ActionPlanPrint,
  useSavedActions,
  PrintFooter,
  trackToolResult,
  Field,
  TextInput,
  type Verdict,
} from "@/components/tools/ui";

/* HSE construction inspection readiness — a self-check against the areas
   front-line HSE inspectors concentrate on during unannounced site visits.

   Grounding: HSE's current inspection drive emphasises occupational health
   (dust/silica/wood, noise, vibration), alongside the perennial physical
   priorities — work at height, welfare, site organisation/public protection
   and asbestos. Inspectors check active, maintained controls and evidence of
   competence, not just paperwork. Enforcement magnets (work at height,
   welfare, dust, asbestos) carry extra weight here.

   This is a readiness prompt, not a prediction of any inspection outcome. */

type Answer = "yes" | "partly" | "no";

interface Question {
  id: string;
  weight: 1 | 2; // 2 = enforcement-magnet area
  area: string;
  /** The yes/no/partly prompt shown to the user. */
  prompt: string;
  /** One line on what an inspector expects to see if this is weak. */
  inspectorExpects: string;
}

const QUESTIONS: Question[] = [
  {
    id: "work-at-height",
    weight: 2,
    area: "Work at height",
    prompt:
      "Is every work-at-height task properly protected — edge protection, secured ladders/towers, scaffold inspected within the last 7 days, and no work on or near fragile surfaces without controls?",
    inspectorExpects:
      "Collective protection first (edge protection, guard rails), current scaffold/tower inspection records, and fragile-roof areas physically guarded.",
  },
  {
    id: "welfare",
    weight: 2,
    area: "Welfare facilities",
    prompt:
      "Are adequate welfare facilities in place and usable — toilets, hot and cold washing water, somewhere to take breaks and dry/change clothing — from the first day on site?",
    inspectorExpects:
      "Clean toilets, hot water for washing, a heated break area and drying facilities available before work starts, not 'on order' (CDM 2015 Schedule 2).",
  },
  {
    id: "dust",
    weight: 2,
    area: "Dust control & RPE",
    prompt:
      "Is construction dust controlled at source — water suppression or on-tool extraction (M-class) for cutting/grinding/chasing — with face-fit-tested RPE where dust still escapes?",
    inspectorExpects:
      "On-tool extraction or water suppression in use, not dry cutting; RPE that is face-fit tested and clean-shaven users. Silica and wood dust are a current HSE priority.",
  },
  {
    id: "asbestos",
    weight: 2,
    area: "Asbestos",
    prompt:
      "Before any refurbishment or demolition of a pre-2000 building, is a refurbishment/demolition asbestos survey in place and have workers had asbestos awareness training?",
    inspectorExpects:
      "A refurbishment & demolition survey for pre-2000 buildings, the findings briefed to those at risk, and asbestos awareness training records.",
  },
  {
    id: "public-protection",
    weight: 2,
    area: "Site organisation & public protection",
    prompt:
      "Is the site secured against unauthorised access and the public protected — hoarding/fencing, controlled entry, and protection from falling materials and site traffic at the perimeter?",
    inspectorExpects:
      "A secured perimeter, no gaps a child could enter, and the public kept clear of falling-material and vehicle hazards (children on site is a serious finding).",
  },
  {
    id: "housekeeping",
    weight: 1,
    area: "Housekeeping",
    prompt:
      "Are walkways, stairs and work areas kept clear of trip hazards, trailing leads, off-cuts and waste, with materials stored tidily?",
    inspectorExpects:
      "Clear access routes, managed waste, and storage that doesn't create slips, trips or falling-object risk.",
  },
  {
    id: "plant-pedestrian",
    weight: 1,
    area: "Plant & vehicle/pedestrian segregation",
    prompt:
      "Are vehicles and pedestrians kept apart — defined walkways, controlled reversing, a banksman where needed, and plant operated only by trained, authorised operators?",
    inspectorExpects:
      "Physical separation of people and plant, managed reversing/blind spots, and evidence of operator competence for excavators, telehandlers, MEWPs etc.",
  },
  {
    id: "vibration-noise",
    weight: 1,
    area: "Vibration & noise",
    prompt:
      "Are hand-arm vibration and noise exposures managed — trigger times limited, lower-vibration tools chosen, hearing protection in noisy areas, and health surveillance where required?",
    inspectorExpects:
      "Exposure kept below the action values where practicable, suitable hearing protection in use, and health surveillance for regularly exposed workers.",
  },
  {
    id: "coshh",
    weight: 1,
    area: "COSHH",
    prompt:
      "Are hazardous substances assessed under COSHH, with safety data sheets, the right controls and PPE/RPE in place, and assessments that match what's actually used on site?",
    inspectorExpects:
      "Task-based COSHH assessments matching the real substances, controls actually in use (not just listed), and correct, maintained PPE/RPE.",
  },
  {
    id: "manual-handling",
    weight: 1,
    area: "Manual handling",
    prompt:
      "Has manual handling been designed out where reasonably practicable — mechanical aids, smaller loads, two-person lifts — rather than relying on workers to 'lift carefully'?",
    inspectorExpects:
      "Evidence the risk has been reduced at source (delivery to point of use, lifting aids), not just a generic 'lift correctly' instruction.",
  },
  {
    id: "competence-induction",
    weight: 1,
    area: "Competence, training & induction",
    prompt:
      "Has everyone on site had a site-specific induction, and can you show competence/training records (and CSCS or equivalent) for the work being done?",
    inspectorExpects:
      "A site induction for every worker and visitor, plus training/competence records matched to the tasks people are actually doing.",
  },
  {
    id: "rams-in-place",
    weight: 1,
    area: "RAMS in place & followed",
    prompt:
      "Are task-specific risk assessments and method statements on site, briefed to the workers doing the job, and is work actually being done the way the RAMS describes?",
    inspectorExpects:
      "Site-specific RAMS (not boilerplate), signed briefing records, and work on the ground matching the documented method.",
  },
  {
    id: "rams-current",
    weight: 1,
    area: "RAMS currency & competence",
    prompt:
      "Do your RAMS show who prepared them and when, and have they been reviewed to reflect changes to the site, sequence, equipment or workforce?",
    inspectorExpects:
      "Authorship, date and basis recorded; documents updated for changes. Static templates older than ~6 months are a red flag.",
  },
  {
    id: "first-aid",
    weight: 1,
    area: "First aid & emergency arrangements",
    prompt:
      "Are first-aid provision and emergency arrangements in place and known — trained first-aider(s), stocked kit, nearest A&E identified, and an escape/assembly plan briefed at induction?",
    inspectorExpects:
      "Adequate first-aiders and kit for the numbers and risk, a clear emergency/rescue plan, and workers who know what to do.",
  },
  {
    id: "fire",
    weight: 1,
    area: "Fire safety",
    prompt:
      "Are fire risks managed — controlled hot works, ignition sources kept from combustibles, clear escape routes, suitable extinguishers and a means of raising the alarm?",
    inspectorExpects:
      "Hot-works control, protected escape routes, appropriate fire-fighting equipment and a workable means of alerting everyone on site.",
  },
];

const SCORE: Record<Answer, number> = { yes: 1, partly: 0.5, no: 0 };

const ANSWER_OPTIONS: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "partly", label: "Partly" },
  { value: "no", label: "No" },
];

export function HseInspectionChecker() {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [siteName, setSiteName] = useState("");
  // Owner/due per failed check — turns the gap list into an action plan.
  const [actions, setAction, clearActions] = useSavedActions("hse-inspection-checker");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const resultTracked = useRef(false);

  const setAnswer = (id: string, value: Answer) =>
    setAnswers((a) => ({ ...a, [id]: value }));

  const computed = useMemo(() => {
    const answered = QUESTIONS.filter((q) => answers[q.id] !== undefined);
    if (answered.length === 0) return null;

    const maxWeight = QUESTIONS.reduce((s, q) => s + q.weight, 0);
    const gained = QUESTIONS.reduce(
      (s, q) => s + (answers[q.id] !== undefined ? SCORE[answers[q.id]] * q.weight : 0),
      0
    );
    const pct = Math.round((gained / maxWeight) * 100);

    // Gaps: anything not a clear "yes". Weighted (enforcement-magnet) first,
    // then "no" before "partly", so the most exposed items surface at the top.
    const gaps = QUESTIONS.filter(
      (q) => answers[q.id] === "no" || answers[q.id] === "partly"
    ).sort((a, b) => {
      if (a.weight !== b.weight) return b.weight - a.weight;
      const rank = (id: string) => (answers[id] === "no" ? 0 : 1);
      return rank(a.id) - rank(b.id);
    });

    const unanswered = QUESTIONS.length - answered.length;
    const hasWeightedNo = QUESTIONS.some((q) => q.weight === 2 && answers[q.id] === "no");

    let level: Verdict;
    let title: string;
    let advice: string;

    if (hasWeightedNo || pct < 50) {
      level = "veryhigh";
      title = `${pct}% ready — serious gaps to close first`;
      advice =
        "One or more high-risk areas an inspector targets first are not under control. These are the kinds of issues that attract enforcement action — sort the priority items below before work continues.";
    } else if (pct < 75) {
      level = "high";
      title = `${pct}% ready — important gaps remain`;
      advice =
        "The basics are partly there, but there are gaps in areas inspectors look at closely. Work through the priority actions below and re-check.";
    } else if (pct < 90) {
      level = "medium";
      title = `${pct}% ready — a few things to tighten up`;
      advice =
        "Most areas are in reasonable shape. Close out the remaining items below so nothing obvious is left for an inspector to find.";
    } else {
      level = "low";
      title = `${pct}% ready — strong, keep it maintained`;
      advice =
        "Your answers suggest the key inspection areas are well managed. Keep controls live and records current — readiness is about what's happening on the ground today, not just the paperwork.";
    }

    return { gaps, pct, level, title, advice, unanswered, answeredCount: answered.length };
  }, [answers]);

  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("hse-inspection-checker", { score: computed.pct });
    }
  }, [computed]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Questions */}
      <div className="space-y-6">
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Would your site stand up to an unannounced visit?</h2>
        <p className="mt-1 text-sm text-muted">
          Answer honestly for the site as it is right now. &ldquo;Partly&rdquo; means it&apos;s in place
          but inconsistent or incomplete. Items marked{" "}
          <span className="font-semibold text-risk-veryhigh">priority</span> are the areas HSE inspectors
          act on first.
        </p>

        <div className="mt-5 space-y-4">
          {QUESTIONS.map((q, i) => {
            const current = answers[q.id];
            return (
              <div key={q.id} className="rounded-xl border border-line bg-mist/60 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">
                    {i + 1}. {q.area}
                  </span>
                  {q.weight === 2 && (
                    <span className="rounded-full bg-risk-veryhigh/10 px-2 py-0.5 text-[0.68rem] font-bold uppercase tracking-wide text-risk-veryhigh">
                      Priority
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[0.92rem] leading-snug text-body">{q.prompt}</p>
                <div className="mt-3 grid grid-cols-3 gap-2" role="group" aria-label={q.prompt}>
                  {ANSWER_OPTIONS.map((opt) => {
                    const selected = current === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAnswer(q.id, opt.value)}
                        aria-pressed={selected}
                        className={
                          "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors " +
                          (selected
                            ? "border-emerald bg-emerald-tint text-emerald-deep"
                            : "border-line-2 bg-white text-ink hover:border-ink")
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PhotoEvidence photos={photos} onChange={setPhotos} />
      </div>

      {/* Result + printable action list */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="hse-inspection-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                HSE inspection readiness check
              </div>
              <h2 className="mt-1 text-lg">
                {siteName ? `${siteName} — ` : ""}
                {new Date().toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={computed.level} title={computed.title}>
                  {computed.advice}
                </ResultBanner>
              </div>

              <p className="mt-3 text-[0.78rem] text-muted">
                Based on {computed.answeredCount} of {QUESTIONS.length} questions answered
                {computed.unanswered > 0
                  ? ` — ${computed.unanswered} still to answer.`
                  : "."}{" "}
                This is a readiness prompt to help you prepare, not a prediction of any inspection
                outcome.
              </p>

              {computed.gaps.length > 0 ? (
                <div className="mt-5">
                  <div className="text-[0.8rem] font-bold uppercase tracking-wide text-ink">
                    Priority actions
                  </div>
                  <ol className="mt-3 space-y-3">
                    {computed.gaps.map((q) => {
                      const isNo = answers[q.id] === "no";
                      return (
                        <li key={q.id} className="border-b border-line/60 pb-3 last:border-0 last:pb-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-ink">{q.area}</span>
                            {q.weight === 2 && (
                              <span className="rounded-full bg-risk-veryhigh/10 px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-wide text-risk-veryhigh">
                                Priority
                              </span>
                            )}
                            <span
                              className={
                                "rounded-full px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-wide " +
                                (isNo
                                  ? "bg-risk-veryhigh/10 text-risk-veryhigh"
                                  : "bg-[#fdf6e3] text-[#8a6400]")
                              }
                            >
                              {isNo ? "Not in place" : "Partly"}
                            </span>
                          </div>
                          <p className="mt-1 text-[0.84rem] leading-snug text-muted">
                            <span className="font-semibold text-ink">What an inspector expects:</span>{" "}
                            {q.inspectorExpects}
                          </p>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              ) : (
                <p className="mt-5 text-sm text-body">
                  No gaps flagged in the questions you answered. Keep the controls live and the records
                  current.
                </p>
              )}

              <ActionPlanPrint
                items={computed.gaps.map((q) => ({
                  id: q.id,
                  label: `${q.area} — ${answers[q.id] === "no" ? "put controls in place" : "complete the partial controls"}`,
                }))}
                values={actions}
              />

              <PhotoEvidencePrint photos={photos} />

              <PrintFooter toolName="HSE inspection readiness checker" />
            </div>

            <div className="card shadow-soft p-5 print-hide">
              <label className="block">
                <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">
                  Site name (for the printed action list)
                </span>
                <input
                  type="text"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="e.g. 14 Mill Lane refurbishment"
                  className="w-full rounded-lg border border-line-2 bg-white px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25"
                />
              </label>
            </div>

            <ActionPlanner
              items={computed.gaps.map((q) => ({
                id: q.id,
                label: `${q.area} (${answers[q.id] === "no" ? "not in place" : "partly in place"})`,
              }))}
              values={actions}
              onChange={setAction}
              onClearAll={clearActions}
            />

            <div className="flex justify-end">
              <PrintButton tool="hse-inspection-checker" label="Print / save action list as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the questions to get your readiness score and a printable priority-action list, with
            what an HSE inspector expects to see for each gap.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Photo evidence (client-side only — images never leave the browser) ──── */

interface Photo {
  id: number;
  dataUrl: string;
  caption: string;
}

const MAX_PHOTOS = 6;
let nextPhotoId = 1;

/** Downscales an image file to a JPEG data URL (longest edge ≤ 1200px). */
function downscaleToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const maxEdge = 1200;
        const longest = Math.max(img.width, img.height);
        const scale = longest > maxEdge ? maxEdge / longest : 1;
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("no canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

function PhotoEvidence({ photos, onChange }: { photos: Photo[]; onChange: (p: Photo[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [atCap, setAtCap] = useState(false);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      setAtCap(true);
      return;
    }
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const taken = images.slice(0, room);
    setAtCap(images.length > room);
    const added: Photo[] = [];
    for (const file of taken) {
      try {
        const dataUrl = await downscaleToDataUrl(file);
        added.push({ id: nextPhotoId++, dataUrl, caption: "" });
      } catch {
        /* skip files that fail to decode */
      }
    }
    if (added.length > 0) onChange([...photos, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  }

  const update = (id: number, caption: string) =>
    onChange(photos.map((p) => (p.id === id ? { ...p, caption } : p)));
  const remove = (id: number) => {
    onChange(photos.filter((p) => p.id !== id));
    setAtCap(false);
  };

  return (
    <div className="card shadow-soft p-6 print-hide">
      <h2 className="text-xl">Photo evidence</h2>
      <p className="mt-1 text-sm text-muted">
        Photos of the conditions backing up your findings — they print with the action list.
      </p>
      <div className="mt-4">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={photos.length >= MAX_PHOTOS}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add photos
        </button>
      </div>
      <p className="mt-2 text-[0.78rem] text-muted">
        Photos stay in this browser — they are never uploaded.
      </p>
      {atCap && (
        <p className="mt-1.5 text-[0.8rem] font-semibold text-[#b34e10]">
          You can attach up to {MAX_PHOTOS} photos — the rest were left out.
        </p>
      )}
      {photos.length > 0 && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {photos.map((p) => (
            <div key={p.id} className="rounded-xl border border-line bg-mist/60 p-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={p.dataUrl} alt="" className="h-36 w-full rounded-lg object-cover" />
              <div className="mt-2">
                <Field label="Caption">
                  <TextInput
                    value={p.caption}
                    onChange={(e) => update(p.id, e.target.value)}
                    placeholder="What this photo shows"
                  />
                </Field>
              </div>
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="mt-2 text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoEvidencePrint({ photos }: { photos: Photo[] }) {
  if (photos.length === 0) return null;
  return (
    <div className="mt-5">
      <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
        Photo evidence
      </h3>
      <div className="mt-2 grid grid-cols-2 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="avoid-break">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={p.dataUrl} alt="" className="w-full rounded-lg border border-line" />
            {p.caption.trim() && (
              <p className="mt-1 text-[0.82rem] leading-snug text-body">{p.caption}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
